import BigNumber from 'bignumber.js';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { StrategyWeightResponse } from '../../../../server/response/responseScheme';
import Box from '../../../uikit/Box';
import { apiClient } from '../../../clientUtils/apiClient';
import { useMobxInstance } from '../../../clientUtils/mobxUtil';

class ViewStore {
    latestWeight: StrategyWeightResponse | null = null;
    holdings: {
        ticker: string;
        name: string; // TODO: 종목명 조회 필요
        weight: number;
    }[] = [];
    isLoading = true;

    constructor() {
        makeAutoObservable(this);
    }

    async fetch(strategyHash: string) {
        this.isLoading = true;

        try {
            const latestWeight = await apiClient.strategy.public.getLatestWeight.$query({
                hash: strategyHash,
            });

            runInAction(() => {
                this.latestWeight = latestWeight;
                this.isLoading = false;
                this.holdings = this.calculateHoldings();
            });
        } catch (error) {
            console.error('Failed to fetch latest weight:', error);
            runInAction(() => {
                this.isLoading = false;
                this.holdings = [];
            });
        }
    }

    private calculateHoldings() {
        if (!this.latestWeight || !this.latestWeight.weightsJson) {
            return [];
        }

        const holdings = Object.entries(this.latestWeight.weightsJson).map(([ticker, weight]) => ({
            ticker,
            name: ticker, // TODO: 종목명 조회 필요
            weight: weight as number,
        }));

        // 비중이 높은 순으로 정렬
        holdings.sort((a, b) => b.weight - a.weight);

        return holdings;
    }
}

interface Props {
    strategyHash: string;
}

const StrategyHoldingsBox = observer(({ strategyHash }: Props) => {
    const viewStore = useMobxInstance(ViewStore);

    useEffect(() => {
        viewStore.fetch(strategyHash);
    }, [viewStore, strategyHash]);

    if (viewStore.isLoading) {
        return <Box isLoading={true} />;
    }

    // BigNumber로 정확한 합계 계산
    const totalWeight = viewStore.holdings
        .reduce((sum, h) => sum.plus(h.weight), new BigNumber(0))
        .toNumber();
    const cashWeight = new BigNumber(1).minus(totalWeight).toNumber();

    return (
        <Box>
            <Box.Title title="종목 구성" />
            <div className="flex flex-col gap-5">
                {viewStore.holdings.map((holding) => {
                    return (
                        <div key={holding.ticker} className="flex flex-col gap-1.5">
                            {/* 종목 정보 행 */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <span className="text-lg font-bold">{holding.ticker}</span>
                                    <span className="text-base-content/60 text-sm">
                                        {holding.name}
                                    </span>
                                </div>
                                <div className="text-xl font-bold tabular-nums">
                                    {Math.round(holding.weight * 100)}%
                                </div>
                            </div>

                            {/* 비중 바 */}
                            <div className="bg-base-200 relative h-1.5 overflow-hidden rounded-full">
                                <div
                                    className="absolute top-0 left-0 h-full bg-gray-400 transition-all"
                                    style={{ width: `${holding.weight * 100}%` }}
                                ></div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* 현금 비중 */}
            {cashWeight > 0 && (
                <div className="bg-base-200 mt-4 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                        <span className="text-base-content/80 font-medium">💵 현금</span>
                        <span className="text-base-content/80 font-semibold tabular-nums">
                            {(cashWeight * 100).toFixed(1)}%
                        </span>
                    </div>
                </div>
            )}
        </Box>
    );
});

export default StrategyHoldingsBox;
